<template>
  <!-- 一般商品詳情頁 -->
  <div class="scroll_flex_container product_detail_container" style="background-color: white;">
    <div class="column_center_start product_info">
      <!-- 有圖片時：保持原本排版 -->
      <div
        v-if="productImage"
        ref="productImageWrapper"
        class="sticky z-10 w-full aspect-square bg-white product_img relative"
        :style="imageStickyStyle"
      >
        <div
          ref="productImageCarousel"
          class="product_image_carousel"
          @touchstart.passive="resetImageAutoPlay"
          @mousedown="resetImageAutoPlay"
          @scroll.passive="handleProductImageScroll"
        >
          <div
            v-for="(image, imageIndex) in carouselImages"
            :key="`${image.url}-${imageIndex}`"
            class="product_image_slide"
          >
            <img :src="image.url" class="w-full h-full object-cover" />
          </div>
        </div>
        
        <div class="fixed top-2 right-2 m-2">
          <div class="row_center_center" style="background-color: #DDDDDD; border-radius: 50%; padding: 2px;"
            @click="closeProductDetail">
            <text id="close" class="button-icon"
              style="color: #000; font-size: 32px; height: 32px; width: 32px">close</text>
          </div>
        </div>

        <div class="row_center_around absolute bottom-0 left-0 w-full px-4 pb-3 pt-10 bg-gradient-to-t from-black/35 to-transparent">
          <text class="text-white text-2xl font-semibold text_three_lines text-left flex-grow mr-2">
            {{ getFrontEndName(product.name) }}
          </text>
          
          <text class="text-[#FD5001] font-bold text-xl">
            ${{ totalPrice }}
          </text>
        </div>
      </div>

      <!-- 沒有圖片時：新排版 -->
      <div v-else class="sticky top-0 z-10 w-full h-[110px] bg-white product_img relative">
        <div class="fixed top-2 right-2 m-2 ">
          <div class="row_center_center" style="background-color: #DDDDDD; border-radius: 50%; padding: 2px;"
            @click="closeProductDetail">
            <text id="close" class="button-icon"
              style="color: #000; font-size: 32px; height: 32px; width: 32px">close</text>
          </div>
        </div>

        <div class="row_center_around w-full absolute bottom-0 left-0  px-4 pb-3">
          <text class="text-black text-2xl font-semibold text_three_lines text-left flex-grow mr-2">
            {{ getFrontEndName(product.name) }}
          </text>
          
          <text class="text-[#FD5001] font-bold text-xl">
            ${{ totalPrice }}
          </text>
        </div>
      </div>

      <div
        class="relative z-0 bg-white w-full px-1 py-6 -mt-4"
        style="width: 100%; flex-grow: 1; padding-bottom: 30px;"
      >
        <div v-if="couponExchange" class="coupon_exchange_notice">
          使用「{{ couponExchange.couponTitle }}」兌換；規格加價需另外支付
        </div>
        <div v-if="productImages.length > 1" class="product_image_thumbnails">
          <button
            v-for="(image, imageIndex) in productImages"
            :key="`thumbnail-${image.url}-${imageIndex}`"
            type="button"
            class="product_image_thumbnail"
            :class="{ active: selectedImageIndex === imageIndex }"
            @click="setSelectedProductImage(imageIndex)"
          >
            <img :src="image.url" :alt="`${product.name} 圖片 ${imageIndex + 1}`" />
          </button>
        </div>
        <div class="description">
          <!-- HTML 內容渲染 -->
          <div 
            v-if="isHtmlDescription" 
            v-html="sanitizedProductDescription"
            class="html-description"
          ></div>
          
          <!-- 純文字內容渲染 -->
          <span v-else>{{ sanitizedProductDescription }}</span>
        </div>
        <div class="column_center_center" style="width: 100%;" v-if="productSpecSettings.length">
          <div class="column_center_center" style="width: 92%;" v-for="(spec, specIndex) in productSpecSettings"
            :key="specIndex">
            <div class="row_center_start" style="width: 100%; padding: 15px 0">
              <text class="title">{{ specDisplayName(spec) }}</text>
              <text class="subTitle" v-if="spec.mustHave">必選</text>
              <text class="subTitle" v-if="!spec.isSingleSelection">複選[最少{{
                spec.multiSelectionMin }}, 最多{{ spec.multiSelectionMax }}]{{
                  spec.allowSelectManyTimes ? '(可重複選)' : '' }}</text>
            </div>
            <div class="column_center_center w-full" v-if="!spec.allowSelectManyTimes">
              <!-- 單選 / 複選欄位 -->
              <productDetailSingleSubOption class="column_start_start option"
                :class="{ active: isSelected(specIndex, subSpecIndex) }"
                v-for="(subSpec, subSpecIndex) in spec.specOptions" :key="subSpecIndex" :product="subSpec"
                :isSelected="isSelected(specIndex, subSpecIndex)" cancelable isSpecOption
                @click="spec.multiSelectionMax === 1 || spec.isSingleSelection ? selectedSingleItem(specIndex, subSpecIndex) : selectedMultipleItems(specIndex, subSpecIndex)" />

            </div>

            <div class="column_center_start w-full" v-if="spec.allowSelectManyTimes">
              <!-- 可重複選欄位 -->
              <productDetailMultiSubOption class="column_center_start option"
                :class="{ active: isSelected(specIndex, subSpecIndex) }"
                v-for="(subSpec, subSpecIndex) in spec.specOptions" :key="subSpecIndex" :product="subSpec"
                isSpecOption
                @addQuantity="addOptionQuantity(specIndex, subSpecIndex)"
                @subtractQuantity="subtractOptionQuantity(specIndex, subSpecIndex)"
                :quantity="checkOptionQuantity(specIndex, subSpecIndex)" />
            </div>
          </div>
        </div>
      </div>
      <div class="column_start_start w-full mt-5 mb-4 px-[3%]">
        <text class="title">備註</text>
        <div class="w-full order_note" :class="noteTextAreaFocus ? 'border-[#FD5001]' : 'border-[#DDDDDD]'"
          @click="noteTextAreaFocus != noteTextAreaFocus">
          <textarea class="" :maxlength="productNoteMaxLength" :placeholder="productNotePlaceHolder" v-model="note"
            v-on:focus="noteTextAreaFocus = true" v-on:blur="noteTextAreaFocus = false"></textarea>
          <div class="w-full row_center_end">
            <p class="text-sm text-[#666666] h-5">{{ note.length }}/{{ productNoteMaxLength }}</p>
          </div>
        </div>
      </div>
    </div>
    <AddToCart :quantity="quantity" :isQuantityLocked="!!couponExchange"
      :buttonText="couponExchange ? '加入兌換商品' : '加入購物車'" @addToCart="addToCart"
      @updateQuantity="updateQuantity"></AddToCart>
  </div>
</template>
<script>
import addToCart from "@/components/store/addToCart.vue";
import productDetailMultiSubOption from "@/components/store/productDetailMultiSubOption.vue";
import productDetailSingleSubOption from "@/components/store/productDetailSingleSubOption.vue";
import commonUtilities from "@/utils/common.utils";
import { isHtmlContent, sanitizeHtml } from "@/utils/htmlSanitizer.utils";
import { getFrontEndName, getSpecFrontEndName } from "@/utils/productName.utils";
import { getInitialProductSpecSettings } from "@/utils/couponExchange.utils";
import { mapGetters } from 'vuex';
export default {
  components: {
    AddToCart: addToCart,
    productDetailSingleSubOption,
    productDetailMultiSubOption

  },
  props: {
    product: {
      type: Object,
      default: () => {
        return {};
      },
      required: true,
    },
    // 兌換模式：{ couponTitle, quantity }；加入時改 emit confirmExchange，由結帳頁套用優惠券
    couponExchange: {
      type: Object,
      default: null,
    },
  },
  emits: ["close", "confirmExchange"],
  data() {
    return {
      // 使用者輸入的數量, 如果輸入數量為0, 則顯示 props quantity
      quantity: this.couponExchange?.quantity || 1,
      selectedImageIndex: 0,
      imageStickyTopOffset: 100,
      imageAutoPlayTimer: null,
      imageLoopResetTimer: null,
      isImageLooping: false,
      isProgrammaticImageScroll: false,
      imageScrollUnlockTimer: null,
      showMarkUpOptions: [],
      selectedOption: [],
      showCustomizePopup: false,
      productSpecSettings: [],
      note: "",
      noteTextAreaFocus: false
    };
  },
  computed: {
    imageStickyStyle() {
      return { top: `-${this.imageStickyTopOffset}px` }
    },
    productNoteMaxLength() {
      return this.$store.state.order.productNoteMaxLength
    },
    productNotePlaceHolder() {
      return this.$store.state.order.productNotePlaceHolder
    },
    productImage() {
      return this.productImages[this.selectedImageIndex]?.url || this.productImages[0]?.url || ''
    },
    productImages() {
      const images = this.$store.getters['product/getProductImagesById'](this.product.id)
      if (images.length) {
        return images.map(image => ({
          ...image,
          url: image.image_url
        }))
      }

      const fallbackImage = this.$store.getters['product/getProductImageById'](this.product.id)
      return fallbackImage ? [{ url: fallbackImage }] : []
    },
    carouselImages() {
      if (this.productImages.length <= 1) return this.productImages

      return [
        { ...this.productImages[this.productImages.length - 1], isClone: true },
        ...this.productImages,
        { ...this.productImages[0], isClone: true }
      ]
    },
    productDescription() {
      // 使用新的 getter 來取得描述，會自動判斷使用哪個資料來源
      return this.$store.getters['product/getProductDescById'](this.product.id)
    },
    sanitizedProductDescription() {
      let rawDescription = this.$store.getters['product/getProductDescById'](this.product.id)
      
      if (!rawDescription) return ''
      
      try {
       rawDescription = rawDescription.replace(/<br><\/p>/g, '<br><br></p>')
        return sanitizeHtml(rawDescription)
      } catch (error) {
        console.warn('HTML 淨化失敗，回退到純文字模式:', error)
        return rawDescription.replace(/<[^>]*>/g, '') // 移除所有 HTML 標籤
      }
    },
    isHtmlDescription() {
      const rawDescription = this.$store.getters['product/getProductDescById'](this.product.id)
      return isHtmlContent(rawDescription)
    }, 
    totalPrice() {
      const customizedTotalPrice = this.productSpecSettings.reduce((total, spec) => {
        return total += spec.subSpecOptionSelectCount.reduce((total, count, index) => {
          if (count === 0 || !spec.specOptions || !spec.specOptions.length) return total;
          return total += count * spec.specOptions[index].price
        }, 0)
      }, 0)


      return (this.productPrice + customizedTotalPrice) * this.quantity;
    },
    ...mapGetters('spreadsheet', {
      comboSubItems: 'comboSubItems',
      specifications: 'specifications',
      products: 'products'
    }),
    productPrice() {
      return Number(this.product?.price)
    },
    useDbData() {
      return this.$store.state.spreadsheet.useDbData
    },
    productQuantityInCart() {
      return this.$store.getters['cart/productQuantityInCart']
    }
  },
  mounted() {
    console.log('product detail', this.product);
    this.productSpecSettingsInit()
    this.resetProductImageCarouselPosition()
    this.startImageAutoPlay()
    this.$nextTick(() => this.updateImageStickyTop())
    window.addEventListener('resize', this.updateImageStickyTop)
  },
  unmounted() {
    this.stopImageAutoPlay()
    this.clearImageLoopResetTimer()
    this.clearImageScrollUnlockTimer()
    window.removeEventListener('resize', this.updateImageStickyTop)
  },
  watch: {
    'product.id'() {
      this.selectedImageIndex = 0
      this.resetProductImageCarouselPosition()
      this.startImageAutoPlay()
      this.$nextTick(() => this.updateImageStickyTop())
    },
    productImages(images) {
      if (this.selectedImageIndex >= images.length) {
        this.selectedImageIndex = 0
      }
      this.resetProductImageCarouselPosition()
      this.startImageAutoPlay()
      this.$nextTick(() => this.updateImageStickyTop())
    }
  },
  methods: {
    updateImageStickyTop() {
      const wrapper = this.$refs.productImageWrapper
      if (!wrapper) return
      const visibleHeight = 200
      this.imageStickyTopOffset = Math.max(wrapper.clientHeight - visibleHeight, 0)
    },
    startImageAutoPlay() {
      this.stopImageAutoPlay()
      if (this.productImages.length <= 1) return

      this.imageAutoPlayTimer = window.setInterval(() => {
        this.showNextProductImage()
      }, 5000)
    },
    resetImageAutoPlay() {
      if (this.productImages.length <= 1) return

      this.startImageAutoPlay()
    },
    stopImageAutoPlay() {
      if (!this.imageAutoPlayTimer) return

      window.clearInterval(this.imageAutoPlayTimer)
      this.imageAutoPlayTimer = null
    },
    clearImageLoopResetTimer() {
      if (!this.imageLoopResetTimer) return

      window.clearTimeout(this.imageLoopResetTimer)
      this.imageLoopResetTimer = null
    },
    clearImageScrollUnlockTimer() {
      if (!this.imageScrollUnlockTimer) return

      window.clearTimeout(this.imageScrollUnlockTimer)
      this.imageScrollUnlockTimer = null
    },
    lockProgrammaticImageScroll(duration = 550) {
      this.isProgrammaticImageScroll = true
      this.clearImageScrollUnlockTimer()
      this.imageScrollUnlockTimer = window.setTimeout(() => {
        this.isProgrammaticImageScroll = false
      }, duration)
    },
    showNextProductImage() {
      if (this.productImages.length <= 1) return

      const isLastImage = this.selectedImageIndex === this.productImages.length - 1
      if (isLastImage) {
        this.isImageLooping = true
        this.lockProgrammaticImageScroll()
        this.scrollProductImageToIndex(this.productImages.length + 1, 'smooth')
        this.clearImageLoopResetTimer()
        this.imageLoopResetTimer = window.setTimeout(() => {
          this.selectedImageIndex = 0
          this.scrollProductImageToIndex(1, 'auto')
          this.isImageLooping = false
        }, 500)
        return
      }

      const nextIndex = this.selectedImageIndex + 1
      this.selectedImageIndex = nextIndex
      this.lockProgrammaticImageScroll()
      this.scrollProductImageToIndex(nextIndex + 1, 'smooth')
    },
    handleProductImageScroll() {
      const carousel = this.$refs.productImageCarousel
      if (!carousel || !carousel.clientWidth) return

      const imageIndex = Math.round(carousel.scrollLeft / carousel.clientWidth)
      if (this.productImages.length <= 1) {
        this.selectedImageIndex = imageIndex
        return
      }

      if (this.isImageLooping || this.isProgrammaticImageScroll) return

      if (imageIndex === 0) {
        this.selectedImageIndex = this.productImages.length - 1
        this.clearImageLoopResetTimer()
        this.imageLoopResetTimer = window.setTimeout(() => {
          this.scrollProductImageToIndex(this.productImages.length, 'auto')
        }, 100)
        return
      }

      if (imageIndex === this.productImages.length + 1) {
        this.selectedImageIndex = 0
        this.clearImageLoopResetTimer()
        this.imageLoopResetTimer = window.setTimeout(() => {
          this.scrollProductImageToIndex(1, 'auto')
        }, 100)
        return
      }

      this.selectedImageIndex = imageIndex - 1
    },
    setSelectedProductImage(index) {
      this.resetImageAutoPlay()

      if (this.selectedImageIndex === this.productImages.length - 1 && index === 0 && this.productImages.length > 1) {
        this.showNextProductImage()
        return
      }

      if (this.selectedImageIndex === 0 && index === this.productImages.length - 1 && this.productImages.length > 1) {
        this.selectedImageIndex = index
        this.isImageLooping = true
        this.lockProgrammaticImageScroll()
        this.scrollProductImageToIndex(0, 'smooth')
        this.clearImageLoopResetTimer()
        this.imageLoopResetTimer = window.setTimeout(() => {
          this.scrollProductImageToIndex(this.productImages.length, 'auto')
          this.isImageLooping = false
        }, 500)
        return
      }

      this.selectedImageIndex = index
      this.lockProgrammaticImageScroll()
      this.$nextTick(() => {
        this.scrollProductImageToIndex(this.productImages.length > 1 ? index + 1 : index, 'smooth')
      })
    },
    resetProductImageCarouselPosition() {
      this.$nextTick(() => {
        this.scrollProductImageToIndex(this.productImages.length > 1 ? this.selectedImageIndex + 1 : this.selectedImageIndex, 'auto')
      })
    },
    scrollProductImageToIndex(index, behavior = 'smooth') {
      const carousel = this.$refs.productImageCarousel
      if (!carousel) return

      carousel.scrollTo({
        left: carousel.clientWidth * index,
        behavior
      })
    },
    productSpecSettingsInit() {
      this.productSpecSettings = getInitialProductSpecSettings(this.product, this.specifications)
      console.log('productSpecSettings', this.productSpecSettings)
    },
    checkOptionQuantity(specIndex, subSpecIndex) {
      return this.productSpecSettings[specIndex].subSpecOptionSelectCount[subSpecIndex]
    },
    addOptionQuantity(specIndex, subSpecIndex) {
      // 複選 ＆ 可重複選則加點數量
      // 檢查是否超過最大選擇數
      const currentSpecSelectCount = this.productSpecSettings[specIndex].subSpecOptionSelectCount.reduce((acc, el) => acc + el, 0)
      const currentSpecSelectionMax = this.productSpecSettings[specIndex].multiSelectionMax
      if (currentSpecSelectCount >= currentSpecSelectionMax) {
        commonUtilities.createToast("warning", `最多選擇${currentSpecSelectionMax}個`, "")
        return
      }
      this.productSpecSettings[specIndex].subSpecOptionSelectCount[subSpecIndex]++
    },
    subtractOptionQuantity(specIndex, subSpecIndex) {
      const currentSpecSelectCount = this.productSpecSettings[specIndex].subSpecOptionSelectCount.reduce((acc, el) => acc + el, 0)
      // 如果加點數量為 0 或是該選項沒有被選中，則不做任何事
      if (currentSpecSelectCount === 0 || this.productSpecSettings[specIndex].subSpecOptionSelectCount[subSpecIndex] === 0) {
        return
      }
      // 如果加點數量為1，則移除選項
      if (currentSpecSelectCount == 1) {
        this.productSpecSettings[specIndex].subSpecOptionSelectCount[subSpecIndex]--
        return
      }
      // 如果加點數量大於1，則數量減一，並移除其中一個選項
      if (currentSpecSelectCount > 1) {
        this.productSpecSettings[specIndex].subSpecOptionSelectCount[subSpecIndex]--
      }
    },
    toBoolean(value) {
      return (/true/).test(value)
    },
    selectedSingleItem(specIndex, subSpecIndex) {
      // 單選
      const hasSelected = this.productSpecSettings[specIndex].subSpecOptionSelectCount[subSpecIndex] > 0

      if (!hasSelected) {
        // 將選到的 subSpecOptionSelectCount[subSpecIndex] 設為 1，其餘設為0
        this.productSpecSettings[specIndex].subSpecOptionSelectCount = this.productSpecSettings[specIndex].subSpecOptionSelectCount.map((el, i) => {
          return i === subSpecIndex ? 1 : 0
        })
      } else {
        this.productSpecSettings[specIndex].subSpecOptionSelectCount[subSpecIndex] = 0
      }
    },
    selectedMultipleItems(specIndex, subSpecIndex) {
      // 複選
      // 檢查選項是否已經被選中
      const hasSelected = this.productSpecSettings[specIndex].subSpecOptionSelectCount[subSpecIndex] > 0
      const totalCount = this.productSpecSettings[specIndex].subSpecOptionSelectCount.reduce((acc, el) => acc + el, 0)

      if (!hasSelected) {
        // 檢查是否超過最大選擇數
        if (totalCount >= this.productSpecSettings[specIndex].multiSelectionMax) {
          commonUtilities.createToast("warning", `最多選擇${this.productSpecSettings[specIndex].multiSelectionMax}個`, "")
          return
        }
        // 如果沒有被選中，則添加到選中的選項中
        this.productSpecSettings[specIndex].subSpecOptionSelectCount[subSpecIndex]++
      } else {
        // 如果已經被選中，則從選中的選項中移除
        this.productSpecSettings[specIndex].subSpecOptionSelectCount[subSpecIndex]--
      }
    },
    isSelected(specIndex, subSpecIndex) {
      return this.productSpecSettings[specIndex].subSpecOptionSelectCount[subSpecIndex] > 0
    },
    closeProductDetail() {
      this.$emit("close");
    },
    updateQuantity(value) {
      this.quantity = value
    },
    cartValidator() {
      if (this.quantity == 0) {
        throw "請選擇數量"
      }
      this.validateStockQuantity(this.product, this.quantity)

      for (const option of this.productSpecSettings) {
        const displayName = this.specDisplayName(option)
        const currentOptionsCount = option.subSpecOptionSelectCount.reduce((acc, el) => acc + el, 0)
        // 如果是必選
        if (option.mustHave && currentOptionsCount === 0) {
          throw `${displayName}尚未選擇`
        }
        // 如果是單選並且是必選
        if (option.mustHave && option.isSingleSelection && currentOptionsCount !== 1) {
          throw `${displayName}尚未選擇`
        }
        // 如果是複選 or 可重複選，加總各項的 quantity 是否符合 minSelect, maxSelect
        if (!option.isSingleSelection) {
          if (currentOptionsCount < option.multiSelectionMin) {
            throw `${displayName}尚未選擇`
          }
          if (currentOptionsCount > option.multiSelectionMax) {
            throw `${displayName}選擇數量超過最大值`
          }
        }
      }
    },
    getFrontEndName,
    specDisplayName(spec) {
      return getSpecFrontEndName(spec)
    },
    validateStockQuantity(product, addQuantity) {
      if (!this.useDbData || Number(product?.stockMode) !== 1) return
      const stockCount = Number(product.stockCount || 0)
      const currentCartQuantity = this.productQuantityInCart(product.id)
      if (currentCartQuantity + Number(addQuantity || 0) > stockCount) {
        throw this.formatStockLimitMessage(product, currentCartQuantity, stockCount)
      }
    },
    formatStockLimitMessage(product, currentCartQuantity, stockCount) {
      const availableQuantity = Math.max(stockCount - currentCartQuantity, 0)
      return `${getFrontEndName(product.name)}剩餘數量不足\n目前購物車：${currentCartQuantity}\n本次最多可加：${availableQuantity}`
    },
    addToCart() {
      try {
        this.cartValidator()
      } catch (error) {
        commonUtilities.createToast("warning", error)
        return
      }

      const orderItem = {
        product: this.product,
        quantity: this.quantity,
        isCombo: false,
        settings: this.productSpecSettings,
        note: this.note
      }
      console.log('加入購物車', orderItem)
      if (this.couponExchange) {
        this.$emit("confirmExchange", orderItem);
      } else {
        this.$store.dispatch("cart/addToCart", orderItem);
      }
      this.$emit("close");
    }
  },
};
</script>
<style lang="scss" scoped>
@import '@/static/css/html-content-fix.css';

.coupon_exchange_notice {
  margin: 0 3% 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background-color: #fff1e8;
  color: #9a3412;
  font-size: 14px;
}
.product_detail_container {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.product_info {
  width: 100%;
  background-color: #fff;
  // flex-shrink: 0;
  overflow-y: auto;
  flex-grow: 1;
}

.product_img {
  width: 100%;
  flex-shrink: 0;
  font-family: PingFang SC;
  font-weight: 500;
  font-size: 24px;
}

.product_image_carousel {
  display: flex;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.product_image_carousel::-webkit-scrollbar {
  display: none;
}

.product_image_slide {
  width: 100%;
  height: 100%;
  flex: 0 0 100%;
  scroll-snap-align: start;
}

.product_image_thumbnails {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 70px;
  padding: 10px 12px;
  overflow-x: auto;
  background-color: #fff;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.product_image_thumbnails::-webkit-scrollbar {
  display: none;
}

.product_image_thumbnail {
  width: 50px;
  height: 50px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  flex: 0 0 50px;
  background-color: #f5f5f5;
}

.product_image_thumbnail.active {
  border-color: #FD5001;
}

.product_image_thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.description {
  border-bottom: 1px dotted #000;
  padding-bottom: 15px;
  width: 92%;
  margin: 0 auto; 
  font-size: 14px;
  font-weight: 400;
  color: #666666;

  .html-description {
    // 基本文字樣式
    line-height: 1.6;
    word-wrap: break-word;

    b, strong {
      font-weight: bold;
    }
    
    i, em {
      font-style: italic;
    }
    
    u {
      text-decoration: underline;
    }
    
    span {
      // 支援 style 屬性的內聯樣式
      // 允許的 CSS 屬性會通過 HTML 淨化工具處理
      display: inline;
    }

    // 響應式設計支援
    @media (max-width: 768px) {
      font-size: 13px;
    }
  }
}

.subTitle {
  font-size: 14px;
  font-weight: 400;
  font-family: PingFang SC;
  color: #d60c0a;
  margin-left: 10px;
}

.option {
  width: 100%;
  padding: 10px 4%;
  background-color: #eeeeee;
  font-size: 16px;
  font-weight: 400;
  font-family: PingFang SC;
  margin-bottom: 10px;
  border-radius: 6px;

  img {
    width: 32px;
    height: 32px;
  }
}

.option.active {
  border: #FD4F02 2px solid;
  background-color: #fff;
  color: #FD4F02;
}

.customized_options {
  color: #666666;
  font-size: 14px;
  margin-top: 10px;
  width: 100%;
}

.order_note {
  width: 100%;
  padding: 10px 5%;
  height: 120px;
  border-width: 1px;
  border-style: solid;
  border-radius: 12px;
  margin-top: 10px;
  font-size: 18px;
  display: flex;
  flex-direction: column;

  textArea {
    width: 100%;
    flex-grow: 1;
  }

  textArea:focus {
    outline: none;
  }
}
</style>
